import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { VinText } from '@carsys/ui'
import { Box, IconButton, Tooltip } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useNotifier } from '@cbgms/base/notifier'

// Import type only to avoid circular dependency
export interface UseVinUnmaskReturn {
  vinMaskingEnabled: boolean
  unmaskVin: (vin?: string) => Promise<void>
  isUnmasking: boolean
  unmaskedVin: string | null
  UnmaskConfirmationDialog: React.FC<{
    open: boolean
    onClose: () => void
    onConfirm: () => void
    vehicleUUID?: string
    workorderUUID?: string
    registration?: string
    hasVin?: boolean
  }>
  resetUnmaskedVin: () => void
  vinMasked: () => boolean
  resourceType: string | undefined
  resourceID: string | undefined
}

interface Props {
  vin: string
  vehicleUUID: string
  workorderUUID?: string
  updatedAt?: string
  registration?: string
  resourceID?: string
  useVinUnmask: (params: { vin: string; vehicleUUID?: string; workorderUUID?: string; resourceID?: string }) => UseVinUnmaskReturn
}

export function MaskedVehicleVin({ vin, vehicleUUID, workorderUUID, updatedAt, useVinUnmask, registration, resourceID }: Props) {
  const { t } = useTranslation()
  const notifier = useNotifier()
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const { vinMasked, vinMaskingEnabled, unmaskVin, isUnmasking, unmaskedVin, UnmaskConfirmationDialog, resetUnmaskedVin } = useVinUnmask({
    vin,
    vehicleUUID,
    workorderUUID,
    resourceID
  })

  useEffect(() => {
    if (updatedAt) {
      resetUnmaskedVin()
    }
  }, [updatedAt, resetUnmaskedVin])

  const displayVin = unmaskedVin || vin

  const handleUnmaskConfirm = async () => {
    try {
      await unmaskVin(vin)
      notifier.success(t('Vehicles.VinUnmask.Success'))
      setDialogOpen(false)
    } catch (error) {
      console.error('Failed to unmask VIN:', error)
      notifier.error(t('Vehicles.VinUnmask.Error'))
    }
  }

  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <VinText value={displayVin} />
        {!unmaskedVin && vinMaskingEnabled && vinMasked() && (
          <Tooltip title={t('vehicle.show_full_vin')}>
            <IconButton
              size='small'
              color='primary'
              onClick={() => setDialogOpen(true)}
              disabled={isUnmasking}
              sx={{ ml: 'auto', p: 0.5, float: 'right' }}
            >
              <VisibilityIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <UnmaskConfirmationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleUnmaskConfirm}
        vehicleUUID={vehicleUUID}
        workorderUUID={workorderUUID}
        registration={registration}
        hasVin={!!vin}
      />
    </React.Fragment>
  )
}

export default MaskedVehicleVin
